import { db, auth } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';

/**
 * Crear una notificación in-app
 * @param {Object} params - { userId, type, payload }
 * @param {string} params.userId - Destinatario (quién la ve)
 * @param {string} params.type - 'like' | 'comment' | 'order_request' | 'order_accepted' | 'order_rejected' | 'order_payment_requested' | 'order_in_progress' | 'order_completed' | 'order_deliverables_added' | 'order_message' | 'new_follower'
 * @param {Object} params.payload - Datos para mostrar y enlazar
 * @returns {Promise<{ success: boolean, notificationId?: string, error?: string }>}
 */
export const createNotification = async ({ userId, type, payload }) => {
  try {
    if (!userId || !type) {
      return { success: false, error: 'userId y type son requeridos' };
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return { success: false, error: 'No autenticado' };
    }
    const notificationsRef = collection(db, 'notifications');
    const docRef = await addDoc(notificationsRef, {
      userId,
      actorUserId: currentUser.uid,
      type,
      read: false,
      createdAt: serverTimestamp(),
      payload: payload || {}
    });
    return { success: true, notificationId: docRef.id };
  } catch (error) {
    console.error('Error al crear notificación:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Suscripción en tiempo real a las notificaciones del usuario (primeras 20)
 * @param {string} userId - ID del usuario
 * @param {Function} callback - Recibe { notifications, unreadCount }
 * @returns {Function} Función para cancelar la suscripción
 */
export const subscribeToNotifications = (userId, callback) => {
  if (!userId) return () => {};

  const notificationsRef = collection(db, 'notifications');
  const q = query(
    notificationsRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(20)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    const unreadCount = snapshot.docs.filter(d => d.data().read === false).length;
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    callback({ notifications, unreadCount, lastVisible });
  });

  return unsubscribe;
};

/**
 * Obtener notificaciones de un usuario (paginado)
 * @param {string} userId - ID del usuario
 * @param {number} limitCount - Límite por página (default 20)
 * @param {Object} startAfterDoc - DocumentSnapshot para paginación (opcional)
 * @returns {Promise<{ success: boolean, notifications?: Array, lastVisible?: Object, hasMore?: boolean, error?: string }>}
 */
export const getNotifications = async (userId, limitCount = 20, startAfterDoc = null) => {
  try {
    if (!userId) {
      return { success: false, notifications: [], hasMore: false };
    }
    const notificationsRef = collection(db, 'notifications');
    let q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    if (startAfterDoc) {
      q = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(startAfterDoc),
        limit(limitCount)
      );
    }
    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    return {
      success: true,
      notifications,
      lastVisible,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error al obtener notificaciones:', error);
    return { success: false, notifications: [], hasMore: false, error: error.message };
  }
};

/**
 * Obtener cantidad de notificaciones no leídas
 * @param {string} userId - ID del usuario
 * @returns {Promise<{ success: boolean, count?: number, error?: string }>}
 */
export const getUnreadCount = async (userId) => {
  try {
    if (!userId) return { success: true, count: 0 };
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', false),
      limit(500)
    );
    const snapshot = await getDocs(q);
    return { success: true, count: snapshot.size };
  } catch (error) {
    console.error('Error al contar no leídas:', error);
    return { success: false, count: 0, error: error.message };
  }
};

/**
 * Marcar una notificación como leída
 * @param {string} notificationId - ID del documento
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const markAsRead = async (notificationId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'No autenticado' };
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, { read: true });
    return { success: true };
  } catch (error) {
    console.error('Error al marcar como leída:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Crear o actualizar notificación de chat de orden (agrupa por orden: "Chat de X servicio (N)")
 * @param {string} recipientUserId - UID del destinatario (el que no envió el mensaje)
 * @param {string} orderId - ID de la orden
 * @param {string} serviceName - Nombre del servicio
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const createOrUpdateOrderChatNotification = async (recipientUserId, orderId, serviceName) => {
  try {
    if (!recipientUserId || !orderId) {
      return { success: false, error: 'recipientUserId y orderId son requeridos' };
    }
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', recipientUserId),
      where('type', '==', 'order_message'),
      where('orderId', '==', orderId),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const notifDoc = snapshot.docs[0];
      const data = notifDoc.data();
      const currentCount = (data.payload && data.payload.messageCount) || 0;
      await updateDoc(notifDoc.ref, {
        payload: { ...(data.payload || {}), serviceName: serviceName || data.payload?.serviceName, messageCount: currentCount + 1 },
        createdAt: serverTimestamp(),
        read: false
      });
      return { success: true };
    }

    await addDoc(notificationsRef, {
      userId: recipientUserId,
      type: 'order_message',
      orderId,
      read: false,
      createdAt: serverTimestamp(),
      payload: { orderId, serviceName: serviceName || 'Servicio', messageCount: 1 }
    });
    return { success: true };
  } catch (error) {
    console.error('Error al crear/actualizar notificación de chat:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Marcar todas las notificaciones del usuario como leídas
 * @param {string} userId - ID del usuario (debe ser el actual)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const markAllAsRead = async (userId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== userId) {
      return { success: false, error: 'No autorizado' };
    }
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', false),
      limit(500)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { success: true };
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      batch.update(docSnap.ref, { read: true });
    });
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Error al marcar todas como leídas:', error);
    return { success: false, error: error.message };
  }
};
